'use client'

import { captureAttribution } from '@/utils/attribution'
import { trackEvent } from '@/utils/tracking'
import { useEffect } from 'react'

/**
 * Zählt Klicks auf mailto- und tel-Links und hält die Kampagnen-Herkunft der
 * Sitzung fest (captureAttribution, nur mit Werbe-Einwilligung).
 *
 * Hintergrund: Anfragen kommen bei KOMMERS überwiegend per Mail, aber genau
 * dieser Pfad war der einzige ungemessene. Weder Google Ads noch GA4 sahen,
 * ob eine Landingpage eine Mailanfrage ausgelöst hat, während Formular und
 * Calendly längst getrackt werden (siehe utils/tracking.ts).
 *
 * Bewusst ein delegierter Listener auf document statt einer eigenen
 * Link-Komponente: die mailto-Links stehen über gut 20 Stellen verteilt,
 * größtenteils in Server-Komponenten. So greift das Tracking überall und auch
 * bei Links, die später dazukommen.
 *
 * Feuert absichtlich KEINE Ads-Conversion. Ein Mail- oder Telefonklick ist ein
 * Absichts-, kein Abschlusssignal. In Google Ads gehört er als sekundäre Conversion-Action
 * importiert, sonst verwässert er die Optimierung auf echte Leads.
 */
export default function MailtoTracker() {
  useEffect(() => {
    // Einmal beim Laden und erneut, sobald im Banner eingewilligt wird
    captureAttribution()
    const handleConsentUpdate = () => captureAttribution()
    document.addEventListener('cookieyes_consent_update', handleConsentUpdate)

    const handleClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const link = target.closest('a[href^="mailto:"], a[href^="tel:"]')
      if (!link) return

      // Nur der Pfad, nie Adresse oder Nummer: eine Mitarbeiteradresse in
      // einem Analytics-Event wäre personenbezogen.
      const isPhone = link.getAttribute('href')?.startsWith('tel:')
      trackEvent(isPhone ? 'phone_click' : 'mailto_click', { cta_source: window.location.pathname })
    }

    // capture: true, damit der Klick auch dann ankommt, wenn ein Handler
    // weiter unten die Propagation stoppt.
    document.addEventListener('click', handleClick, { capture: true })
    return () => {
      document.removeEventListener('click', handleClick, { capture: true })
      document.removeEventListener('cookieyes_consent_update', handleConsentUpdate)
    }
  }, [])

  return null
}
